import { Node as ReactFlowNode, useStoreApi } from '@xyflow/react';
import { useCallback } from 'react';

import { InternalFlowChartProps, Node } from '../../types';

/**
 * @description Wrapper around the selection-end callback that dismisses React
 * Flow's NodesSelection overlay after a marquee (shift+drag) selection ends.
 *
 * When a marquee finishes with nodes selected, React Flow activates a
 * group-drag rect spanning the selection's bounding box. That rect paints
 * above the nodes and captures their pointer events, so the per-node
 * NodeResizer grips underneath can't be grabbed — which silently disabled
 * bulk resize for marquee selections (T1-6646). Only grips poking outside the
 * rect (the outer corners of a small selection) stayed usable, which is why
 * resizing appeared to work with two tiles but not with many.
 *
 * Dismissing the overlay keeps every selected node's resize grips live, so
 * resizing one selected tile applies to all selected tiles regardless of how
 * many are selected. It is not free, though — the overlay has two real jobs
 * we accept losing, on every FlowChart diagram (waterfall, entity map, family
 * tree, presentation):
 *
 * 1. Drag-by-empty-space: the selection can no longer be dragged by grabbing
 *    the blank area between selected nodes. Dragging any selected node still
 *    moves the whole selection, so group drag remains available.
 * 2. Post-marquee arrow-key nudge: the overlay auto-focuses itself and maps
 *    arrow keys to moving the selection. Without it, nothing holds focus
 *    after a marquee, so keyboard-nudging the selection only works after
 *    focusing a node (and plain-clicking one collapses the multi-selection).
 *
 * Keyboard delete is unaffected (it reads selected elements, not the
 * overlay), and no app code reads nodesSelectionActive.
 */
export function useInternalOnSelectionEnd<T extends ReactFlowNode = Node>({
  onSelectionEnd: onSelectionEndProp,
}: InternalFlowChartProps<T>): InternalFlowChartProps<T>['onSelectionEnd'] {
  const store = useStoreApi();

  const onSelectionEnd = useCallback<
    NonNullable<InternalFlowChartProps<T>['onSelectionEnd']>
  >(
    (...args) => {
      // React Flow sets nodesSelectionActive right before firing
      // onSelectionEnd, so clearing it here reliably wins.
      store.setState({ nodesSelectionActive: false });
      onSelectionEndProp?.(...args);
    },
    [onSelectionEndProp, store]
  );

  return onSelectionEnd;
}
